import { isAxiosError } from "axios";
import api from "../api";
import { ChallengeType } from "./types/challenges.types";

const SUBMIT_MAX_ATTEMPTS = 4;
const RETRYABLE_STATUS = new Set([408, 429, 502, 503, 504]);

function isTransientSubmitError(err: unknown): boolean {
  if (!isAxiosError(err)) {
    return false;
  }
  const status = err.response?.status;
  if (status !== undefined) {
    return RETRYABLE_STATUS.has(status);
  }
  return (
    err.code === "ECONNABORTED" ||
    err.code === "ERR_NETWORK" ||
    err.response === undefined
  );
}

export const getMediaChallengeInfo = async (
  id: number
): Promise<ChallengeType | undefined> => {
  const res = await api.get<ChallengeType>(`media-challenges/${id}`);

  return res.data;
};

export const getUploadMediaChallengeImageUrl = async (
  taskId: number
): Promise<string> => {
  const res = await api.get(
    `media-challenges/tasks-submissions-link/${taskId}`
  );

  return res.data;
};

export const getUploadMediaChallengeVideoUrl = async (
  taskId: number
): Promise<string> => {
  const res = await api.get(
    `media-challenges/tasks-submissions-link/${taskId}?type=video`
  );

  return res.data;
};

export const submitMediaChallenge = async (
  id: number,
  mediaUrl: string,
  customInputs?: { [key: string]: string }
): Promise<void> => {
  const url = `media-challenges/tasks-submissions/${id}`;
  const body = { mediaUrl, customInputs };
  let lastError: unknown;
  for (let attempt = 1; attempt <= SUBMIT_MAX_ATTEMPTS; attempt++) {
    try {
      await api.post(url, body);
      return;
    } catch (err) {
      // Idempotent: batch retry / double "Enviar" after partial success — API returns 409 if task already sent.
      if (isAxiosError(err) && err.response?.status === 409) {
        return;
      }
      lastError = err;
      const canRetry =
        attempt < SUBMIT_MAX_ATTEMPTS && isTransientSubmitError(err);
      if (!canRetry) {
        throw err;
      }
      await new Promise((r) => setTimeout(r, 400 * attempt));
    }
  }
  throw lastError;
};

export type BatchTaskSubmissionItem = {
  taskId: number;
  mediaUrl: string;
  customInputs?: { [key: string]: string };
};

/** One HTTP request for several photo slots (e.g. 5). Same retry/idempotency rules as single submit. */
export const submitMediaChallengeBatch = async (
  submissions: BatchTaskSubmissionItem[]
): Promise<void> => {
  const url = `media-challenges/tasks-submissions/batch`;
  const body = { submissions };
  let lastError: unknown;
  for (let attempt = 1; attempt <= SUBMIT_MAX_ATTEMPTS; attempt++) {
    try {
      await api.post(url, body);
      return;
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        return;
      }
      lastError = err;
      const canRetry =
        attempt < SUBMIT_MAX_ATTEMPTS && isTransientSubmitError(err);
      if (!canRetry) {
        throw err;
      }
      await new Promise((r) => setTimeout(r, 400 * attempt));
    }
  }
  throw lastError;
};

export const deleteTaskSubmission = async (id: number): Promise<void> => {
  return api.delete(`media-challenges/tasks-submissions/${id}`);
};
